import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Segmented, Select, Tag } from 'antd';
import ServiceState from './ServiceState';
import ServiceActions from './ServiceActions';
import BlockedRequests from './BlockedRequests';
import useProjectServices from '../../hooks/useProjectServices';
import useRunnables from '../../hooks/useRunnables';
import {
  formatUptime, getServiceStatus, logsStreamUrl, projectLogsStreamUrl, serviceState, sessionIdFor,
} from '../../services/SandboxService';
import { parseRunnableName, pinnedItems } from '../../utils/runnablesModel';
import FailureLine from './FailureLine';
import NoDockerNote from '../apps/NoDockerNote';
import './sandbox.css';
import './ServiceLogs.css';

const MAX_LINES = 2000;
/** Opened at a moment (Traces → Open logs): the lines this long before and after it. */
export const AROUND_MS = 5000;

/** The lines within AROUND_MS of `at` (epoch ms), every type: what a request that failed then saw. */
export function linesAround(lines, at, aroundMs = AROUND_MS) {
  return lines.filter((l) => {
    const time = Date.parse(l.timestamp);
    return Number.isFinite(time) && Math.abs(time - at) <= aroundMs;
  });
}

/**
 * A project's Logs view (sandbox-first, docs/SANDBOX_FIRST.md): a list to pick all services, one or several, their build
 * or running output live from the preview-runner's stream (several: interleaved by time, each line naming its service),
 * a filter, and the service's controls when one is picked. Works without chat or MCP.
 *
 * The stream's first event carries what the runner kept (`build`, `stdout`, `stderr`), then one event per line;
 * build lines are typed `build`. An older runner sends build lines as `stdout`: they then show under Running.
 * `around` (epoch ms, from Traces → Open logs): only the lines of that moment (±5 s, build and running), from the
 * runner's timed replay, until "Show all".
 */
/**
 * The sessions the view lists: each service, then the files pinned in it (utils/runnablesModel `pinnedItems`, run as
 * sessions of their own, `services/app--compose-docker-compose-yaml`) and any such session the runner still has
 * (unpinned since). `[{ name, label, runnable }]`, `label` as Apps names it ("app · docker-compose.yaml").
 */
export function logSessions(serviceNames, pinned = [], runnerServices = []) {
  const short = (name) => String(name).replace(/^services\//, '');
  const runnables = [
    ...pinned.map((item) => ({ name: item.serviceName, owner: item.service, label: `${short(item.service)} · ${item.file}`, runnable: item })),
    ...runnerServices.filter((entry) => parseRunnableName(entry.serviceName) && !pinned.some((item) => item.serviceName === entry.serviceName))
      .map((entry) => {
        const { service, kind } = parseRunnableName(entry.serviceName);
        return { name: entry.serviceName, owner: service, label: `${short(service)} · ${kind}`, runnable: null };
      }),
  ];
  const listed = serviceNames.flatMap((name) => [
    { name, label: name, runnable: null },
    ...runnables.filter((item) => item.owner === name),
  ]);
  runnables.filter((item) => !serviceNames.includes(item.owner)).forEach((item) => listed.push(item));
  return listed.filter((item, index) => listed.findIndex((other) => other.name === item.name) === index)
    .map(({ name, label, runnable }) => ({ name, label, runnable }));
}

/** The lines the runner's status kept (`buildLogs`, `recentLogs.stdout` / `.stderr`), as the stream's would be. */
export function statusLines(info, now = new Date().toISOString()) {
  if (!info) { return []; }
  const text = (line) => (typeof line === 'string' ? line : line?.message ?? line?.line ?? '');
  return [
    ...(info.buildLogs || []).map((line) => ({ type: 'build', line: text(line), timestamp: line?.timestamp || now })),
    ...(info.recentLogs?.stdout || []).map((line) => ({ type: 'stdout', line: text(line), timestamp: now })),
    ...(info.recentLogs?.stderr || []).map((line) => ({ type: 'stderr', line: text(line), timestamp: now })),
  ];
}

/** `?service=` for what the view shows: `all`, one session, or several joined by commas. */
export function pickToParam(pick) {
  return pick === 'all' ? 'all' : (pick || []).join(',');
}

/** What `?service=` asks for, among the sessions listed: 'all', the known names it lists, or null (none known). */
export function pickFromParam(value, names) {
  if (!value) { return null; }
  if (value === 'all') { return 'all'; }
  const wanted = [...new Set(String(value).split(','))].filter((name) => names.includes(name));
  return wanted.length ? wanted : null;
}

/**
 * Several services' lines in one list, by time (`byService`: name → lines in arrival order). A line without a time
 * keeps its place after the line before it; same time, the service listed first, then arrival.
 */
export function mergeByTime(byService, names) {
  const all = [];
  names.forEach((service, rank) => {
    let last = 0;
    (byService[service] || []).forEach((line, seq) => {
      const time = Date.parse(line.timestamp);
      if (Number.isFinite(time)) { last = time; }
      all.push({ line: { ...line, service }, time: last, rank, seq });
    });
  });
  all.sort((a, b) => a.time - b.time || a.rank - b.rank || a.seq - b.seq);
  return all.map((item) => item.line);
}

// Colours that read on the dark log box and differ clearly from each other (hue steps, not shades of one).
export const SOURCE_COLOURS = ['#6cb6ff', '#ffa657', '#7ee787', '#f778ba', '#e3d26f', '#56d4dd', '#d2a8ff', '#ff7b72', '#a5d6ff', '#c9d1d9'];

const hashOf = (text) => [...String(text)].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) >>> 0, 7);

/**
 * One colour per service, from its name (the same after a reload, wherever it sits in the list); two services whose
 * names land on the same colour: the one first by name keeps it, the other takes the next free one.
 */
export function serviceColours(names) {
  const taken = new Set();
  const out = {};
  [...new Set(names)].sort().forEach((name) => {
    let index = hashOf(name) % SOURCE_COLOURS.length;
    for (let tries = 0; taken.has(index) && tries < SOURCE_COLOURS.length; tries += 1) { index = (index + 1) % SOURCE_COLOURS.length; }
    taken.add(index);
    out[name] = SOURCE_COLOURS[index];
  });
  return out;
}
const ALL = '__all__';
const shortName = (label) => String(label).replace(/^services\//, '');

/**
 * A stream event as the view's lines: `{ reset }` (the runner's replay, its first event), `{ lines }` (one live line),
 * `{ missing: true }` (a multiplexed stream's session the runner does not have yet), else null.
 */
export function readLogEvent(data, now = new Date().toISOString()) {
  if (!data || typeof data !== 'object') { return null; }
  if (data.type === 'missing') { return { missing: true }; }
  if (data.type === 'initial') {
    // with their real times (a runner that keeps them): what "around a moment" needs
    if (Array.isArray(data.timed) && data.timed.length) {
      return { reset: data.timed.map((l) => ({ type: l.type, line: l.line, timestamp: l.timestamp })).slice(-MAX_LINES) };
    }
    return {
      reset: [
        ...(data.build || []).map((line) => ({ type: 'build', line: typeof line === 'string' ? line : line?.message || '', timestamp: line?.timestamp || now })),
        ...(data.stdout || []).map((line) => ({ type: 'stdout', line, timestamp: now })),
        ...(data.stderr || []).map((line) => ({ type: 'stderr', line, timestamp: now })),
      ].slice(-MAX_LINES),
    };
  }
  if (data.line !== undefined) { return { lines: [{ type: data.type, line: data.line, timestamp: data.timestamp }] }; }
  return null;
}

const parseData = (text) => { try { return JSON.parse(text); } catch { return null; } };
const EVENT_SOURCE_CLOSED = 2;
// Pauses before opening a stream again that was answered with an error (the browser retries only dropped ones).
export const RETRY_MS = [1000, 2000, 5000, 10000, 15000];

/**
 * An EventSource that comes back: the browser reconnects a dropped stream itself, never one answered with an error
 * (a sandbox waking: 503; an old per-session stream: 404 until the service starts), which is reopened here after a
 * pause. `onNeverOpened()` (the first attempt failed before it opened) resolves true to give up. Returns its close.
 */
function liveSource(url, { onOpen, onMessage, onDown, onNeverOpened }) {
  let closed = false;
  let source = null;
  let timer = null;
  let attempt = 0;
  let everOpened = false;
  const open = () => {
    source = new EventSource(url, { withCredentials: true });
    source.onopen = () => { if (!closed) { everOpened = true; attempt = 0; onOpen?.(); } };
    source.onmessage = (event) => { if (!closed) { onMessage(event); } };
    source.onerror = () => {
      if (closed) { return; }
      onDown?.();
      if (source.readyState !== EVENT_SOURCE_CLOSED) { return; }
      source.close();
      const again = () => {
        if (closed) { return; }
        timer = setTimeout(open, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]);
        attempt += 1;
      };
      if (!everOpened && attempt === 0 && onNeverOpened) {
        Promise.resolve(onNeverOpened()).then((giveUp) => { if (!giveUp) { again(); } }, again);
        return;
      }
      again();
    };
  };
  open();
  return () => { closed = true; clearTimeout(timer); source?.close(); };
}

/** True when the runner has no such stream (404/405: one older than the multiplexed stream). */
async function streamUnknown(url) {
  const controller = new AbortController();
  try {
    const response = await fetch(url, { credentials: 'include', headers: { Accept: 'text/event-stream' }, signal: controller.signal });
    return response.status === 404 || response.status === 405;
  } catch {
    return false;
  } finally {
    controller.abort();
  }
}

/**
 * The sessions' output, live, in ONE connection (the runner's multiplexed stream): a browser on HTTP/1.1 (a local
 * cluster through a port-forward) holds 6 connections per origin, so one EventSource per service on "All services"
 * left the others, and every other request of the page, waiting: nothing updated. A runner without that stream:
 * one per session, as before. Handlers get the session's name. Returns the close.
 */
export function openLogStreams(projectId, names, { onReset, onLines, onLive }) {
  let stopped = false;
  let close = () => {};
  const deliver = (name, data) => {
    const read = readLogEvent(data);
    if (!read) { return; }
    if (read.missing) { onLive(name, false); return; }
    onLive(name, true);
    if (read.reset) { onReset(name, read.reset); } else { onLines(name, read.lines); }
  };
  const onePerSession = () => {
    const closers = names.map((name) => liveSource(logsStreamUrl(sessionIdFor(projectId, name)), {
      onOpen: () => onLive(name, true),
      onDown: () => onLive(name, false),
      onMessage: (event) => deliver(name, parseData(event.data)),
    }));
    close = () => closers.forEach((closeOne) => closeOne());
  };
  const nameOf = Object.fromEntries(names.map((name) => [sessionIdFor(projectId, name), name]));
  const url = projectLogsStreamUrl(projectId, Object.keys(nameOf));
  close = liveSource(url, {
    onDown: () => names.forEach((name) => onLive(name, false)),
    onMessage: (event) => {
      const data = parseData(event.data);
      const name = data && nameOf[data.session];
      if (name) { deliver(name, data); }
    },
    onNeverOpened: async() => {
      if (!(await streamUnknown(url)) || stopped) { return false; }
      onePerSession();
      return true;
    },
  });
  return () => { stopped = true; close(); };
}

/**
 * Why a session cannot run, when it is Docker that is off: the reason (`'plan'`, `'cluster'`, `'type'`) for one that needs
 * Docker (a pinned compose file or Dockerfile, or a service whose way to run needs it: vfs-service builders
 * `needsDocker`) and is not running, where the pod has no Docker (`docker` false from vfs-service `GET /runnables`;
 * without a reason: the plan's, as Apps reads it). Else null. The log stream itself never says (a session that cannot
 * start is only `missing`).
 */
export function dockerOffOfSession(name, { docker, dockerOff, builders, state }) {
  if (docker !== false || state === 'running') { return null; }
  const needs = Boolean(parseRunnableName(name)) || Boolean(builders?.[name]?.needsDocker);
  return needs ? (dockerOff || 'plan') : null;
}

export default function ServiceLogs({ projectId, serviceNames: ownNames, initialService = null, onServiceChange, around = null }) {
  // The project's services from the pod it runs in (its owner's, per person); a viewer reads the logs, no controls.
  const { services: projectServices, canRun, refresh } = useProjectServices(projectId, { intervalMs: 5000 });
  // The files pinned in the services (a compose file, a Dockerfile) run as sessions of their own: listed too.
  const runnables = useRunnables(projectId);
  const pinned = useMemo(() => pinnedItems(runnables.scan, runnables.choices), [runnables.scan, runnables.choices]);
  const sessions = useMemo(() => logSessions(ownNames, pinned, projectServices), [ownNames, pinned, projectServices]);
  const serviceNames = useMemo(() => sessions.map((item) => item.name), [sessions]);
  const labelOf = (name) => sessions.find((item) => item.name === name)?.label || name;
  // What the view shows: 'all' or a list of sessions (one, or several interleaved by time).
  const asked = pickFromParam(initialService, serviceNames);
  const askedKey = asked ? pickToParam(asked) : '';
  const [pick, setPick] = useState(asked || (serviceNames.length ? 'all' : null));
  const [kind, setKind] = useState('run');
  const [filter, setFilter] = useState('');
  const [linesBy, setLinesBy] = useState({});
  const [live, setLive] = useState({});
  // Each open session's runner status (read once when it opens): its buildError when the list has none.
  const [statusBy, setStatusBy] = useState({});
  const [at, setAt] = useState(around);
  useEffect(() => { setAt(around); }, [around]);
  const boxRef = useRef(null);
  const stickToBottom = useRef(true);
  const picked = useMemo(
    () => (pick === 'all' ? serviceNames : (pick || []).filter((name) => serviceNames.includes(name))),
    [pick, serviceNames],
  );
  const pickedKey = picked.join('\n');
  const single = picked.length === 1 ? picked[0] : null;
  const entryOf = (name) => projectServices.find((s) => s.serviceName === name) || null;
  // A failed build always says why (under its badge, and in place of an empty log), never "No output yet".
  const failureOf = (name) => {
    const entry = entryOf(name);
    if (serviceState(entry) !== 'failed') { return null; }
    const info = statusBy[name] || null;
    return { ...(info || {}), ...entry, buildError: entry?.buildError || info?.buildError, crashOutput: entry?.crashOutput || info?.crashOutput };
  };

  useEffect(() => {
    // The services arrive after the first render on a cold load: the ones asked for, else all of them.
    if (!pick && serviceNames.length) { setPick(asked || 'all'); }
  }, [pick, serviceNames, asked]);

  // A Logs button for another service while this view is open (or just opened on a stale URL): follow it. A pinned
  // file's session is known once the runnables are read: followed then too.
  useEffect(() => {
    if (asked) { setPick(asked); }
  }, [askedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // One connection for every session shown (openLogStreams); a session added or removed opens it again, the lines of
  // the ones kept stay until their replay comes.
  const closeStreamRef = useRef(null);
  const followedRef = useRef([]);
  const streamedRef = useRef(new Set());
  useEffect(() => {
    const before = followedRef.current;
    const gone = before.filter((name) => !picked.includes(name));
    const added = picked.filter((name) => !before.includes(name));
    followedRef.current = picked;
    if (gone.length) {
      const without = (prev) => Object.fromEntries(Object.entries(prev).filter(([name]) => !gone.includes(name)));
      gone.forEach((name) => streamedRef.current.delete(name));
      setLinesBy(without);
      setLive(without);
      setStatusBy(without);
    }
    added.forEach((name) => {
      setLinesBy((prev) => ({ ...prev, [name]: [] }));
      setLive((prev) => ({ ...prev, [name]: false }));
      // What the runner's status kept, until the stream says (a session whose stream does not speak, e.g. one that
      // failed before it ran, still shows its build and its output).
      getServiceStatus(sessionIdFor(projectId, name))
        .then((info) => {
          if (!followedRef.current.includes(name)) { return; }
          setStatusBy((prev) => ({ ...prev, [name]: info }));
          if (!streamedRef.current.has(name)) {
            const kept = statusLines(info);
            if (kept.length) { setLinesBy((prev) => ({ ...prev, [name]: kept.slice(-MAX_LINES) })); }
          }
        })
        .catch(() => {});
    });
    if (!gone.length && !added.length && closeStreamRef.current) { return; }
    closeStreamRef.current?.();
    closeStreamRef.current = picked.length ? openLogStreams(projectId, picked, {
      onLive: (name, value) => setLive((prev) => (prev[name] === value ? prev : { ...prev, [name]: value })),
      onReset: (name, batch) => {
        streamedRef.current.add(name);
        setLinesBy((prev) => ({ ...prev, [name]: batch }));
      },
      onLines: (name, batch) => {
        streamedRef.current.add(name);
        setLinesBy((prev) => {
          const next = (prev[name] || []).concat(batch);
          return { ...prev, [name]: next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next };
        });
      },
    }) : null;
  }, [projectId, pickedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    closeStreamRef.current?.();
    closeStreamRef.current = null;
    followedRef.current = [];
    streamedRef.current.clear();
    setLinesBy({});
    setLive({});
    setStatusBy({});
  }, [projectId]);

  const lines = useMemo(() => (single ? (linesBy[single] || []) : mergeByTime(linesBy, picked)), [linesBy, single, pickedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const wanted = filter.trim().toLowerCase();
    const pool = at ? linesAround(lines, at) : lines.filter((l) => (kind === 'build' ? l.type === 'build' : l.type !== 'build'));
    return pool.filter((l) => !wanted || String(l.line).toLowerCase().includes(wanted));
  }, [lines, kind, filter, at]);

  useEffect(() => {
    const box = boxRef.current;
    if (box && stickToBottom.current) { box.scrollTop = box.scrollHeight; }
  }, [shown]);

  const choose = (next) => { setPick(next); onServiceChange?.(pickToParam(next)); };
  const onPick = (values) => {
    const wasAll = pick === 'all';
    if (!wasAll && values.includes(ALL)) { choose('all'); return; }
    const names = values.filter((value) => value !== ALL);
    // Nothing picked, or every one picked: all of them.
    choose(!names.length || names.length === serviceNames.length ? 'all' : names);
  };

  const dockerOffOf = (name) => dockerOffOfSession(name, {
    docker: runnables.docker, dockerOff: runnables.dockerOff, builders: runnables.scan?.builders, state: serviceState(entryOf(name)),
  });
  const dockerBlocked = picked.filter((name) => dockerOffOf(name));
  const dockerLine = (name, withName) => (
    <span className="svc-logs-failure" data-testid="svc-logs-docker-off" key={`docker-${name}`}>
      {withName && <strong>{shortName(labelOf(name))} </strong>}
      Docker is off in this sandbox. <NoDockerNote reason={dockerOffOf(name)} />
    </span>
  );

  const connectedCount = picked.filter((name) => live[name]).length;
  // Nothing to show and every session shown failed: their reasons take the empty log's place.
  const allFailed = picked.length > 0 && picked.every((name) => failureOf(name));
  const download = () => {
    const text = shown.map((l) => `${l.timestamp || ''} ${single ? '' : `${shortName(labelOf(l.service))} `}${l.type} ${l.line}`).join('\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectId}-${single ? String(single).replace(/\//g, '-') : pick === 'all' ? 'all-services' : 'services'}-${kind}.log`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!serviceNames.length) {
    return <div className="svc-logs-empty">This project has no services yet, so there are no logs to show.</div>;
  }

  const colours = serviceColours(serviceNames);
  const colourOf = (name) => colours[name] || SOURCE_COLOURS[0];
  // The list's tags in each service's colour (the lines' and the summary's), "All services" plain.
  const tagRender = ({ value, label, closable, onClose }) => (
    <Tag
      className="svc-logs-tag"
      closable={closable}
      onClose={onClose}
      onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
      style={value === ALL ? undefined : { borderColor: colourOf(value), boxShadow: `inset 3px 0 0 ${colourOf(value)}` }}
    >
      {label}
    </Tag>
  );
  return (
    <div className="svc-logs">
      <div className="svc-logs-bar">
        <Select
          aria-label="Services"
          className="svc-logs-service-select sbx-mono"
          data-testid="svc-logs-services"
          maxTagCount={3}
          mode="multiple"
          onChange={onPick}
          optionFilterProp="search"
          options={[
            { value: ALL, search: 'all services', label: <span data-testid="svc-logs-tab-all">All services</span> },
            ...serviceNames.map((name) => ({ value: name, search: labelOf(name), label: <span className="sbx-mono" data-testid={`svc-logs-tab-${name}`}>{labelOf(name)}</span> })),
          ]}
          placeholder="Pick services"
          showSearch
          tagRender={tagRender}
          value={pick === 'all' ? [ALL] : picked}
        />
        <Segmented
          onChange={setKind}
          options={[{ value: 'run', label: 'Running' }, { value: 'build', label: 'Build' }]}
          value={kind}
        />
        <Input allowClear aria-label="Filter lines" className="svc-logs-filter" onChange={(e) => setFilter(e.target.value)} placeholder="Filter lines (e.g. error)" value={filter} />
        <span className="svc-logs-right">
          <span className={`sbx-pill ${connectedCount ? 'sbx-pill-running' : ''}`}>
            {!connectedCount ? 'Not connected' : connectedCount === picked.length ? 'Live' : `Live ${connectedCount} of ${picked.length}`}
          </span>
          <Button disabled={!shown.length} onClick={download} size="small">Download</Button>
          {canRun && single && <ServiceActions onChanged={refresh} projectId={projectId} runnable={sessions.find((item) => item.name === single)?.runnable || null} service={entryOf(single)} serviceName={single} />}
        </span>
      </div>
      <BlockedRequests />
      {at && (
        <div className="svc-logs-around" data-testid="svc-logs-around" role="status">
          <span>Lines around {new Date(at).toLocaleTimeString()} (5 s before and after), build and running</span>
          <Button onClick={() => setAt(null)} size="small">Show all</Button>
        </div>
      )}
      {single ? (
        <div className="svc-logs-summary">
          <strong>{labelOf(single)}</strong>
          <ServiceState service={entryOf(single)} />
          {/* Uptime only while it runs: a stopped service is not "up 43 min". */}
          {entryOf(single)?.startedAt && serviceState(entryOf(single)) === 'running' && <span data-testid="svc-logs-uptime">up {formatUptime(entryOf(single).startedAt)}</span>}
          {failureOf(single) && <FailureLine className="svc-logs-failure" service={failureOf(single)} testId="svc-logs-failure" />}
          {!failureOf(single) && dockerOffOf(single) && dockerLine(single, false)}
        </div>
      ) : (
        <div className="svc-logs-summary" data-testid="svc-logs-summary-many">
          {picked.map((name) => (
            <span className="svc-logs-summary-item" key={name}>
              <span aria-hidden="true" className="svc-logs-swatch" style={{ background: colourOf(name) }} />
              <strong>{shortName(labelOf(name))}</strong>
              <ServiceState service={entryOf(name)} />
            </span>
          ))}
          {picked.filter((name) => failureOf(name)).map((name) => (
            <span className="svc-logs-failure" data-testid="svc-logs-failure" key={`failure-${name}`}>
              <strong>{shortName(labelOf(name))}</strong>{' '}<FailureLine service={failureOf(name)} />
            </span>
          ))}
          {dockerBlocked.filter((name) => !failureOf(name)).map((name) => dockerLine(name, true))}
        </div>
      )}
      <div
        aria-label="Log lines"
        aria-live="polite"
        className="svc-logs-box"
        onScroll={(e) => {
          const box = e.currentTarget;
          stickToBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
        }}
        ref={boxRef}
        role="log"
      >
        {shown.length === 0 ? (
          <span className="svc-logs-none">
            {at ? 'Nothing printed in the 5 seconds around that moment.'
              : allFailed ? picked.map((name) => (
                <span className="svc-logs-none-failure" key={name}>{!single && <strong>{shortName(labelOf(name))} </strong>}<FailureLine service={failureOf(name)} /></span>
              ))
                : dockerBlocked.length && dockerBlocked.length === picked.length ? `Nothing to show: Docker is off in this sandbox, so ${single ? 'it' : 'they'} can't start.`
                  : kind === 'build' ? 'No build output yet.' : single ? 'No output yet. Start the service to see what it prints.' : 'No output yet. Start the services to see what they print.'}
          </span>
        ) : shown.map((l, index) => (
          <div className={`svc-logs-line svc-logs-${l.type}`} key={`${index}-${l.timestamp}`}>
            <span className="svc-logs-time">{l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : ''}</span>
            {!single && <span className="svc-logs-source" data-testid="svc-logs-source" style={{ color: colourOf(l.service) }} title={labelOf(l.service)}>{shortName(labelOf(l.service))}</span>}
            <span className="svc-logs-text">{l.line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

ServiceLogs.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialService: PropTypes.string,
  onServiceChange: PropTypes.func,
  // epoch ms: show only the lines around that moment (Traces → Open logs), until "Show all"
  around: PropTypes.number,
};
